import { Component, type ReactNode } from "react";
import { type RouteComponentProps } from "react-router";

import { type BTLoginTypes } from "types/enum";

import { track } from "utilities/analytics/analytics";
import { getGlobalValue } from "utilities/globalValueUtils";
import {
    getJobPickerState,
    getSelectedJobId,
    getSelectedJobIds,
} from "utilities/jobPicker/jobPicker";
import { isInPortal } from "utilities/portal/portal";
import { routesWebforms } from "utilities/routesWebforms";

import { HotkeyDisplaySetup } from "commonComponents/helpers/HotkeyDisplaySetup/HotkeyDisplaySetup";
import {
    type IJobPickerHandler,
    JobPickerHandler,
} from "commonComponents/utilities/JobPicker/JobPicker.api.handler";
import { JobIdTypes } from "commonComponents/utilities/JobPicker/JobPicker.types";

import { type SystemFilterType } from "entity/filters/Filter/Filter.api.types";

export interface IEntityListWrapperState {
    builderId: number;
    jobIds: number[];
    jobName: string;
    userId: number;
    isTemplateMode: boolean;
    loginType: BTLoginTypes;
    jobPickerReady: boolean;
    systemFilter?: SystemFilterType;
    isRouteJobIdDifferent?: boolean;
    allJobsSelected: boolean;
    allBuildersSelected: boolean;
}

interface IEntityListWrapperRouteProps {
    jobId: string;
}

interface IEntityListWrapperProps {
    render: (state: IEntityListWrapperState) => ReactNode;
    isJobPickerRequired: boolean; // Set variable to true if your entity requires the job picker
    reactRouterProps?: RouteComponentProps<IEntityListWrapperRouteProps>;
    jobPickerHandler?: IJobPickerHandler;
}

/* This should only be used on Webforms list pages */

const getJobIds = (hasJobPicker: boolean): number[] => {
    if (!hasJobPicker) {
        return [];
    }

    return getSelectedJobIds();
};

const isAllJobsSelected = (hasJobPicker: boolean): boolean => {
    if (!hasJobPicker) {
        return false;
    }
    return getSelectedJobId(Number(getGlobalValue("getBuilderId"))) === JobIdTypes.AllJobs;
};

export default class EntityListWrapper extends Component<
    IEntityListWrapperProps,
    IEntityListWrapperState
> {
    static defaultProps = {
        isJobPickerRequired: false,
        jobPickerHandler: new JobPickerHandler(),
    };

    state: Readonly<IEntityListWrapperState> = {
        builderId: Number(getGlobalValue("getBuilderId")),
        userId: Number(getGlobalValue("globalUserId")),
        jobIds: getJobIds(this.props.isJobPickerRequired),
        jobName: "",
        isTemplateMode: getJobPickerState().isTemplateMode,
        allBuildersSelected: false,
        loginType: getGlobalValue("loginTypeInt")!,
        jobPickerReady: false,
        systemFilter: parseSystemFilterQS(),
        isRouteJobIdDifferent: undefined,
        allJobsSelected: isAllJobsSelected(this.props.isJobPickerRequired),
    };

    render() {
        if (
            isInPortal({ owner: true }) ||
            this.state.jobPickerReady ||
            !this.props.isJobPickerRequired
        ) {
            return (
                <TrackedWrapper jobIds={this.state.jobIds}>
                    {this.props.render(this.state)}
                    <HotkeyDisplaySetup />
                </TrackedWrapper>
            );
        } else {
            return null;
        }
    }
}

function parseSystemFilterQS() {
    let systemFilter: SystemFilterType | undefined;
    const queryParams = new URLSearchParams(window.location.search);
    if (queryParams.has("filter")) {
        systemFilter = Number(queryParams.get("filter"));
        queryParams.delete("filter");
        routesWebforms.replaceWebformsHistoryState("?" + queryParams.toString());
    }
    return systemFilter;
}

interface ITrackedWrapperProps {
    jobIds?: number[];
    children: React.ReactNode;
}

export const TrackedWrapper: React.FunctionComponent<ITrackedWrapperProps> = track((props) => ({
    jobId: (props.jobIds?.length ?? 0) === 1 ? props.jobIds[0] : 0,
}))((props) => <>{props.children}</>);
